import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { type AxiosRequestConfig } from 'axios';

import { Button, TextField, Col } from 'darrius';

import { notifyErrorClient } from '../../../services/notifyError/client';

import { axiosInstance } from '../../../utils/axiosInstance';
import { onlyNumbers, addPhoneMask } from '../../../utils/parsedMasks';
import { generateHashSha256 } from '../../../utils/generateHash';
import { userDataPatterns } from '../../../utils/fieldPatterns';
import { errorPrefix } from '../../../utils/errorPrefix';

import styles from './FormLead.module.scss';
import { showToast, Toast } from '../../atoms/Toast/Toast';
import { sugestions } from './FormLeadUtils';
import { useRouter } from 'next/router';
import { clientCookie } from '@/utils/clientCookie';

interface IFormLeadProps {
  content: {
    placeholder: string;
    sendButton: string;
  };
  direction: string;
  order: string;
  showLgpdCheckbox?: boolean;
  buttonText: string;
}

interface IFormData {
  email: string;
  phone: string;
  job_board?: {
    partner_id: string;
    job_id: string;
    clickid: string;
    path: string;
  };
}

interface IFieldErrorMessage {
  email: string | null;
  phone: string | null;
}

export const FormLead = (props: IFormLeadProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);

  const emailSugestions = sugestions;

  const {
    content,
    order,
    direction,
    buttonText,
    showLgpdCheckbox = false,
  } = props;

  const initialState = {
    email: '',
    phone: '',
  } as IFormData;

  const [form, setForm] = useState(initialState);
  const [lgpdCheck, setLgpdCheck] = useState(false);
  const [fieldErrorMessage, setFieldErrorMessage] =
    useState<IFieldErrorMessage>({ email: null, phone: null });
  const [disabledButton, setDisabledButton] = useState(true);
  const [shouldTrack, setShouldTrack] = useState(false);

  function handleValidateFields(value, field) {
    if (!value) {
      return 'Campo obrigatório';
    }

    if (
      userDataPatterns[field] &&
      !new RegExp(userDataPatterns[field].regexp).test(value)
    ) {
      return userDataPatterns[field].error;
    }
  }

  function handleFormChange(event) {
    const { name, value } = event.target;
    const errorMessage = handleValidateFields(value, name);
    name == 'email'
      ? setIsSearchDropdownOpen(true)
      : setIsSearchDropdownOpen(false);

    if (errorMessage) {
      setFieldErrorMessage({
        [name as string]: errorMessage,
      } as IFieldErrorMessage);
      setDisabledButton(true);
    } else {
      setIsSearchDropdownOpen(false);
      setFieldErrorMessage({
        [name as string]: null,
      } as IFieldErrorMessage);
      setDisabledButton(false);
    }

    const profileInputData = {
      ...form,
      [name]: value,
    };

    if (name === 'phone') {
      profileInputData[name] = addPhoneMask(value);
    }

    setForm({
      email: profileInputData.email,
      phone: profileInputData.phone,
    });

    if (event.type === 'blur') {
      setShouldTrack(true);
    }
  }

  async function handleSubmit(event) {
    window.tagManager.custom({
      event: 'selfDescribingEvent',
      schema: 'iglu:br.com.getninjas/web_pro_sign_up/jsonschema/1-0-1',
      data: {
        step: 'insert_email_phone',
        action: 'click',
        label: order,
      },
    });
    event.preventDefault();
    event.stopPropagation();

    await submitForm();
  }

  async function submitForm() {
    const category = searchParams.get('category');
    const service = searchParams.get('service');
    const partnerId = searchParams.get('partner_id');
    const clickid = searchParams.get('clickid');
    const jobId = searchParams.get('job_id');

    let data;

    if (partnerId && jobId && clickid) {
      data = {
        job_board: {
          partner_id: partnerId,
          job_id: jobId,
          clickid,
          path: !service
            ? category
            : `seja-um-profissional/${category}/${service}`,
        },
      };
    }

    data = {
      ...data,
      email: form.email.toLocaleLowerCase(),
      phone: onlyNumbers(form.phone),
    };

    const headers = {
      'Accept-Language': 'pt-BR',
      'Content-Type': 'application/json',
      Accept: '*/*',
    };

    const requestOptions: AxiosRequestConfig = {
      headers,
      url: `${process.env.NEXT_PUBLIC_API_PRO}/signup`,
      method: 'POST',
      data,
    };

    try {
      setDisabledButton(true);
      const result = await axiosInstance(requestOptions);
      const { redirectUrl, token, homeAddressForm } = result.data;
      window.tagManager.custom({
        event: 'selfDescribingEvent',
        schema: 'iglu:br.com.getninjas/web_pro_sign_up/jsonschema/1-0-1',
        data: {
          step: 'create',
          action: token,
          label: order,
        },
      });
      window.tagManager.custom({
        event: 'generate_lead',
        prospect_id: token,
        user_id: generateHashSha256(data.email + '55' + data.phone),
      });

      sessionStorage.setItem(
        '@site_profissionais:pre_signup',
        JSON.stringify({ ...form, token, homeAddressForm })
      );

      window.location.href = redirectUrl;
    } catch (error) {
      setDisabledButton(false);
      window.tagManager.custom({
        event: 'selfDescribingEvent',
        schema: 'iglu:br.com.getninjas/web_pro_sign_up/jsonschema/1-0-1',
        data: {
          step: 'insert_email_phone',
          action: 'click',
          label: order,
        },
      });

      notifyErrorClient(errorPrefix('[FORM-LEAD]', error));

      showToast({
        message: 'Não foi possível se cadastrar, tente novamente mais tarde!',
        type: 'error',
      });
    }
    return false;
  }

  const trackingError = useCallback(() => {
    if (fieldErrorMessage.email) {
      window.tagManager.custom({
        event: 'selfDescribingEvent',
        schema: 'iglu:br.com.getninjas/web_pro_sign_up/jsonschema/1-0-0',
        data: {
          step: 'insert_email_phone',
          action: 'error_message',
          state: `invalid_email::${form.email}`,
        },
      });
    } else if (fieldErrorMessage.phone) {
      window.tagManager.custom({
        event: 'selfDescribingEvent',
        schema: 'iglu:br.com.getninjas/web_pro_sign_up/jsonschema/1-0-0',
        data: {
          step: 'insert_email_phone',
          action: 'error_message',
          state: `invalid_phone::${form.phone}`,
        },
      });
    }

    return false;
  }, [fieldErrorMessage, form]);

  function checkIsDisableButton() {
    const checkEmptyFields = !form.email || !form.phone;
    const checkLgpdField = showLgpdCheckbox && !lgpdCheck;

    return disabledButton || checkEmptyFields || checkLgpdField;
  }

  useEffect(() => {
    if (shouldTrack) {
      trackingError();
    }
    setShouldTrack(false);
  }, [shouldTrack, trackingError]);

  return (
    <form
      onSubmit={handleSubmit}
      className={direction}
      name="register-form"
      method="post"
    >
      <Col className="col-sm-12 col-md-4">
        <TextField
          id="email"
          name="email"
          type="email"
          label="E-mail"
          pattern={userDataPatterns.email.regexp}
          placeholder={content.placeholder}
          onChange={handleFormChange}
          errorMessage={fieldErrorMessage.email}
          value={form.email}
        />
        {isSearchDropdownOpen && form.email !== '' && (
          <div className={styles['dropdown']}>
            <ul>
              {emailSugestions
                .filter((email) => {
                  const emailDomain = form.email.split('@')[1];
                  return emailDomain ? email.startsWith(emailDomain) : true;
                })
                .map((email) => (
                  <li
                    key={email}
                    onClick={() => {
                      setForm({
                        ...form,
                        email: `${form.email.split('@')[0]}@${email}`,
                      });
                      setIsSearchDropdownOpen(false);
                      setFieldErrorMessage({
                        ...fieldErrorMessage,
                        email: null,
                      });
                    }}
                  >
                    {form.email.split('@')[0]}@{email}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </Col>

      <Col className="col-sm-12 col-md-4">
        <TextField
          id="phone"
          name="phone"
          type="tel"
          label="Celular"
          pattern={userDataPatterns.phone.regexp}
          placeholder="Ex.: 11 99999-9999"
          onChange={handleFormChange}
          onBlur={handleFormChange}
          errorMessage={fieldErrorMessage.phone}
          value={form.phone}
        />
      </Col>

      {showLgpdCheckbox && (
        <Col
          className={`col-sm-12 col-md-4 form-lead__checkbox-lgpd ${styles['form-lead__checkbox-lgpd']} `}
        >
          <label>
            <input
              type="checkbox"
              checked={lgpdCheck}
              onChange={() => setLgpdCheck(!lgpdCheck)}
            />
            <span>
              Li e concordo com a{' '}
              <a
                target="_blank"
                href="https://www.getninjas.com.br/politica-de-privacidade"
                rel="noreferrer"
                className="form-lead-privacy-policy"
              >
                Política de Privacidade
              </a>{' '}
              e os{' '}
              <a
                target="_blank"
                href="https://www.getninjas.com.br/termos-de-uso"
                rel="noreferrer"
                className="form-lead-privacy-policy"
              >
                Termos de Uso
              </a>
            </span>
          </label>
        </Col>
      )}

      <Col
        className={`col-sm-12 col-md-4 form-lead__button ${styles['form-lead__button']} `}
      >
        <Button.Root
          type="submit"
          disabled={checkIsDisableButton()}
          fullWidth
          variation="primary"
          className="form-lead-start-registration__button"
        >
          {buttonText}
        </Button.Root>
      </Col>

      <Toast />
    </form>
  );
};

export default FormLead;
